import React, { PropsWithChildren } from 'react'
import { alpha, createTheme, StyledEngineProvider, Theme, ThemeProvider } from '@mui/material'

const themeProperties = {
  light: {
    type: 'light',
    background: {
      default: '#FFFFFF',
    },
    primary: {
      main: '#253F80',
      light: '#C4D2EC',
      dark: '#253F80',
    },
    secondary: {
      main: '#5B6271',
      light: '#DEEBF8',
      dark: '#444858', // On Secondary Container
    },
    tertiary: {
      main: '#4C6981',
      light: '#D8F5FF',
      dark: '#264A60',
    },
    error: {
      main: '#B3261E',
      light: '#FCEEEE',
      dark: '#852221',
    },
    success: {
      main: '#2F9567',
      light: '#D3EBD7',
      dark: '#1B6B46',
      variant1: '#71B98A',
      variant2: '#E9F4EB',
      variant3: '#EFF4F0',
    },
    text: {
      primary: '#1B1D20', // M3 On Surface/On Background
      secondary: '#45474F', // M3 On Surface Variant
      onPrimary: '#FFFFFF', // M3 On Primary
    },
    ui: {
      main: '#FFFFFF',
      ui02: '#FAFAFA',
      ui05: '#A1A4B1',
      surface: '#F7FAFF',
      lightSurface: '#F7FBFF',
      surface1: '#edf2fa',
      surface2: '#E8EDF7',
      surfaceDim: '#D8DBE1',
      surfaceVariant: '#FBFDFF',
      surfaceContainer: '#F4F8FD',
      surfaceContainerHigh: '#ECF0F6',
      surfaceContainerLow: '#F9FCFF',
      surfaceContainerLowest: '#FFFFFF',
      surfaceContainerHighest: '#E0E4E9',
      surfaceBright: '#F7FBFF',
      disabledStateColor: 'rgba(27, 29, 32, 0.04)',
      inverseSurface: '#2F3235',
      inverseOnSurface: '#EFF2F7',
      inversePrimary: '#A8C2EA',
      blueTextColor: '#005AB0',
      brand01: '#0078EC',
      primary50: '#4C73B6',
      tertiaryFixedDim: '#B8DFEF',
      onSecondaryFixed: '#191E2B',
      onPrimaryFixedVariant: 'rgba(47, 71, 134)',
      outlineVariant: '#C4C6D0',
    },
    info: {
      main: '#43528D',
      light: '#E1F4FF',
    },
    elevations: {
      elevation1: '0 1px 2px 0 rgba(0, 0, 0, 0.12), 0 1px 3px 1px rgba(0, 0, 0, 0.10)',
      elevation2: '0 1px 2px 0 rgba(0, 0, 0, 0.12), 0 2px 6px 2px rgba(0, 0, 0, 0.10)',
      elevation3: '0 4px 8px 3px rgba(0, 0, 0, 0.12), 0 1px 3px 0 rgba(0, 0, 0, 0.10)',
      elevation4: '0 6px 10px 4px rgba(0, 0, 0, 0.10), 0 2px 3px 0 rgba(0, 0, 0, 0.12)',
      elevation5: '0 8px 12px 6px rgba(0, 0, 0, 0.10), 0 4px 4px 0 rgba(0, 0, 0, 0.12)',
    },
  },
}

export const generateTheme = (variant: ThemeVariant = 'light') => {
  const slimPalette = themeProperties.light
  const newTheme = {
    palette: {
      ...slimPalette,
    },
    typography: {
      fontFamily: 'Work Sans',
      /** M3 Title Large */
      h6: {
        fontWeight: 400,
        fontSize: '20px',
        lineHeight: 1.4,
        letterSpacing: 0,
        color: variant === 'light' ? slimPalette.text.primary : slimPalette.text.onPrimary,
      },
      /** M3 Headline Small */
      h5: {
        fontWeight: 400,
        fontSize: '24px',
        lineHeight: 1.3,
        letterSpacing: 0,
        color: variant === 'light' ? slimPalette.text.primary : slimPalette.text.onPrimary,
      },
      /** M3 Headline Medium */
      h4: {
        fontWeight: 400,
        fontSize: '28px',
        lineHeight: '36px',
        letterSpacing: '0px',
        color: variant === 'light' ? slimPalette.text.primary : slimPalette.text.onPrimary,
      },
      /** M3 Headline Large */
      h3: {
        fontWeight: 400,
        fontSize: '30px',
        lineHeight: '30px',
        letterSpacing: '0px',
        color: variant === 'light' ? slimPalette.text.primary : slimPalette.text.onPrimary,
      },
      /** M3 Label Small Medium */
      caption: {
        fontWeight: 400,
        fontSize: '11px',
        lineHeight: '16px',
        letterSpacing: '0.5px',
        color: variant === 'light' ? slimPalette.text.primary : slimPalette.text.onPrimary,
      },
      /** M3 Title Medium */
      subtitle1: {
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: '0.15px',
        color: variant === 'light' ? slimPalette.text.primary : slimPalette.text.onPrimary,
      },
      /** M3 Title Small */
      subtitle2: {
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: '0.1px',
        color: variant === 'light' ? slimPalette.text.primary : slimPalette.text.onPrimary,
      },
      /** M3 Body Large */
      body1: {
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: '0.25px',
        color: variant === 'light' ? slimPalette.text.primary : slimPalette.text.onPrimary,
      },
      /** M3 Body Medium & M3 Label Large */
      body2: {
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: '0.2px',
        color: variant === 'light' ? slimPalette.text.primary : slimPalette.text.onPrimary,
      },
      /** M3 Label Medium & M3 Body Small (letterSpacing: 0.25px, textTransform: none) */
      overline: {
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: '16px',
        letterSpacing: '0.5px',
        color: variant === 'light' ? slimPalette.text.primary : slimPalette.text.onPrimary,
        textTransform: 'none !important',
      },
    },
    components: {
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            '&:before': {
              height: 0, // Removes the border
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: variant === 'light' ? slimPalette.primary.main : slimPalette.text.onPrimary,
            fontSize: '14px',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '50px',
            textTransform: 'none' as const,
            '&.Mui-disabled': {
              backgroundColor: 'rgba(27, 29, 32, 0.1)',
              color: '#94979c',
            },
            '&.MuiButton-outlined': {
              borderColor: variant === 'light' ? slimPalette.primary.main : slimPalette.text.onPrimary,
              color: variant === 'light' ? slimPalette.primary.main : slimPalette.text.onPrimary,
            },
            '&.MuiButton-text': {
              color: variant === 'light' ? slimPalette.primary.main : slimPalette.text.onPrimary,
            },
            variants: [
              {
                props: { color: 'secondary' as const },
                style: {
                  '&.MuiButton-contained': {
                    color: slimPalette.secondary.dark,
                    backgroundColor: slimPalette.secondary.light,
                    '&.Mui-disabled': {
                      color: alpha(slimPalette.text.secondary, 0.5),
                      backgroundColor: alpha(slimPalette.text.secondary, 0.1),
                    },
                  },
                },
              },
              {
                props: { color: 'tertiary' as const },
                style: {
                  backgroundColor: slimPalette.tertiary.main,
                  color: slimPalette.ui.main,
                },
              },
            ],
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: slimPalette.ui.surfaceVariant,
            boxShadow: slimPalette.elevations.elevation2,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '0.15px',
            color: slimPalette.text.primary,
            padding: '8px 12px',

            '&.Mui-selected, &:hover': {
              backgroundColor: slimPalette.secondary.light,
              color: slimPalette.secondary.dark,
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '28px',
          },
        },
      },
      MuiAlert: {
        variants: [
          {
            props: { severity: 'info' as const },
            style: ({ theme }: { theme: Theme }) => ({
              fontSize: '13px',
              color: slimPalette.text.primary,
              backgroundColor: slimPalette.info.light,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: theme.spacing(1.5),
              '& .MuiLink-root': {
                fontWeight: 500,
                fontSize: '13px',
                lineHeight: '22.1px',
                letterSpacing: '0.17px',
                color: slimPalette.primary.dark,
                textDecoration: 'underline',
              },
            }),
          },
          {
            props: { severity: 'error' as const },
            style: ({ theme }: { theme: Theme }) => ({
              backgroundColor: 'rgba(211, 47, 47, 0.12)',
              color: '#541313',
              borderRadius: theme.spacing(1),
              '& .MuiAlert-icon': {
                color: '#541313',
              },
            }),
          },
        ],
      },
      MuiIconButton: {
        variants: [
          {
            props: { variant: 'contained' as const },
            style: ({ theme }: { theme: Theme }) => ({
              backgroundColor: theme.palette.secondary.light,
              borderRadius: theme.spacing(1),
              padding: '6px 8px',
            }),
          },
        ],
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            height: '64px',
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            color: slimPalette.text.secondary,

            '& .MuiTouchRipple-child': {
              backgroundColor: alpha(slimPalette.secondary.dark, 0.1),
            },
            '& .MuiSvgIcon-root': {
              zIndex: 1, // Show above the :after pseudo-element used for the chip highlight effect
            },

            // M3 Bottom Navigation spec includes a "chip highlight" effect on the selected action that appears behind the icon. This is being implemented using an :after pseudo-element that is styled to create the desired shape and color, and transitions smoothly on hover and selection.
            '&:after': {
              content: '""',
              position: 'absolute',
              top: '4px',
              left: 'calc(50% - (56px / 2))',
              right: 0,
              bottom: 0,
              backgroundColor: 'transparent',
              borderRadius: '16px',
              width: '56px',
              height: '32px',
              transition: 'background-color 0.5s',
            },
            '&:hover:after': {
              backgroundColor: alpha(slimPalette.secondary.dark, 0.08),
            },
            '&:active:after, &:focus:after': {
              backgroundColor: alpha(slimPalette.secondary.dark, 0.1),
            },

            '&.Mui-selected': {
              color: slimPalette.secondary.main,

              '&:after': {
                backgroundColor: slimPalette.secondary.light,
              },
              '&:hover:after': {
                backgroundColor: `color-mix(in srgb, ${slimPalette.secondary.dark} 8%, ${slimPalette.secondary.light} 100%)`,
              },
              '&:active:after, &:focus:after': {
                backgroundColor: `color-mix(in srgb, ${slimPalette.secondary.dark} 10%, ${slimPalette.secondary.light} 100%)`,
              },
            },

            '& .MuiBottomNavigationAction-label': {
              fontWeight: 500,
              fontSize: '12px',
              lineHeight: '24px',
              letterSpacing: '0.5px',

              '&.Mui-selected': {
                fontSize: '12px',
              },
            },
          },
        },
      },
    },
  }

  return newTheme
}
const slimTheme = createTheme(generateTheme())

const MuiThemeProvider: React.FC<PropsWithChildren> = ({ children }) => (
  <StyledEngineProvider injectFirst={true}>
    <ThemeProvider theme={slimTheme}>{children}</ThemeProvider>
  </StyledEngineProvider>
)

export default MuiThemeProvider
